import React from "react";
import styled from "styled-components";
import { BASIC_WHITE } from "../../../constants/colors";
import { Campaign } from "../../../ducks/campaign/types";
import { useAsset } from "../../../hooks/useAsset";
import { Button } from "../../atoms/Button";

type Props = {
  campaigns: Campaign[]; // ソート済みキャンペーン一覧
  handleRegularOrderClick: () => void; // 通常注文を選択
  handleCampaignClick: (campaign: Campaign) => void; // キャンペーンを選択
};

/**
 * キャンペーン選択画面
 *
 * 表示順はsortOrderに従う
 */
export const CampaignSelection: React.FC<Props> = ({
  campaigns,
  handleRegularOrderClick,
  handleCampaignClick,
}) => {
  const { baseDir } = useAsset();

  const getDefaultImage = (type: Campaign["type"]) => {
    switch (type) {
      case "early_bird":
        return `${baseDir}/assets/images/order_early_bird.svg`;
      case "stock_item":
        return `${baseDir}/assets/images/order_stock_item.svg`;
      case "regular":
      default:
        return `${baseDir}/assets/images/order_regular.svg`;
    }
  };

  const getAltText = (type: Campaign["type"]) => {
    switch (type) {
      case "early_bird":
        return "早割でお得！特割なメニューのご注文";
      case "stock_item":
        return "数量限定メニューのご注文";
      case "regular":
      default:
        return "いつものテイクアウトメニューのご注文";
    }
  };

  return (
    <StyledContainer>
      <StyledHeaderText>ご希望のご注文を選択してください</StyledHeaderText>

      <StyledOptionContainer>
        {campaigns.map((campaign) => {
          const isRegular = campaign.type === "regular";
          const isStockItemSoldOut =
            campaign.type === "stock_item" && campaign.isSoldOut;

          return (
            <StyledOption key={campaign.id}>
              <StyledOptionContent>
                <StyledImageContainer
                  onClick={() =>
                    isRegular
                      ? handleRegularOrderClick()
                      : handleCampaignClick(campaign)
                  }
                >
                  <StyledImage
                    src={campaign.image || getDefaultImage(campaign.type)}
                    alt={getAltText(campaign.type)}
                  />
                  {isStockItemSoldOut && (
                    <StyledSoldOutOverlay>
                      <StyledImage
                        src={`${baseDir}/assets/images/limited_stock/soldout.png`}
                        width="288"
                        height="72"
                      />
                    </StyledSoldOutOverlay>
                  )}
                </StyledImageContainer>

                <Button
                  btnStyle="primary"
                  size="full"
                  text="店舗検索へ"
                  onClick={() => {
                    isRegular
                      ? handleRegularOrderClick()
                      : handleCampaignClick(campaign);
                  }}
                  rounded="md"
                />
              </StyledOptionContent>
              {isStockItemSoldOut && <StyledNoClickArea />}
            </StyledOption>
          );
        })}
      </StyledOptionContainer>
    </StyledContainer>
  );
};

// スタイル定義（CSS-in-JS）
const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledHeaderText = styled.h1`
  padding: 13px 28px 8px 28px;
  font-size: 20px;
  font-weight: 700;
  line-height: 29px;
  text-align: center;
  margin: 0;
`;

const StyledOptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 16px;
`;

const StyledOption = styled.div`
  background-color: ${BASIC_WHITE};
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;

const StyledOptionContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledImageContainer = styled.div`
  cursor: pointer;
  /*border-radius: 8px;*/
  overflow: hidden;
  position: relative;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const StyledNoClickArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const StyledSoldOutOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 84%;
  height: auto;
  transform: translate(-50%, -50%);
`;
